import { useState, useRef } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import * as XLSX from 'xlsx';
import styles from './upload_modal.module.css';

const RINGS_DETAIL_HEADERS = [
  'PO Number',
  'Site ID',
  'Project Name',
  'BP',
  'City',
  'Region',
  'Ring Unique Name',
  "Total Length (Mtr's)",
  "On net (Mtr's)",
  "Offnet (Mtr's)",
];

const RING_UNIQUE_NAME_TEMPLATE_HEADER = 'Ring/Section Unique Name';

const getRingsDetailTemplateHeaders = () =>
  RINGS_DETAIL_HEADERS.map((header) =>
    header === 'Ring Unique Name' ? RING_UNIQUE_NAME_TEMPLATE_HEADER : header
  );

const UploadRingsDetailModal = ({ isOpen, onClose, onUpload, isUploading = false, selectedPO }) => {
  const [file, setFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [validationError, setValidationError] = useState(null);
  const fileInputRef = useRef(null);

  const expectedHeaders = RINGS_DETAIL_HEADERS;

  const validateFileFormat = async (selectedFile) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const data = e.target.result;
          let workbook;
          const fileExtension = selectedFile.name
            .substring(selectedFile.name.lastIndexOf('.'))
            .toLowerCase();

          if (fileExtension === '.csv') {
            workbook = XLSX.read(data, { type: 'string', sheetRows: 1 });
          } else {
            workbook = XLSX.read(data, { type: 'array', sheetRows: 1 });
          }

          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, {
            header: 1,
            defval: '',
          });

          if (jsonData.length === 0) {
            reject('File is empty');
            return;
          }

          const fileHeaders = jsonData[0].map((header) => String(header).trim());
          const normalize = (h) => h.toLowerCase().replace(/'/g, '').replace(/\s+/g, ' ').trim();
          const normalizedExpected = expectedHeaders.map(normalize);
          const normalizedFile = fileHeaders.map(normalize);

          const missingHeaders = [];

          if (missingHeaders.length > 0) {
            reject(`File format is incorrect. Missing columns: ${missingHeaders.join(', ')}`);
            return;
          }

          resolve(true);
        } catch (error) {
          reject('Error reading file. Please check the file format.');
        }
      };

      reader.onerror = () => {
        reject('Error reading file. Please try again.');
      };

      const fileExtension = selectedFile.name
        .substring(selectedFile.name.lastIndexOf('.'))
        .toLowerCase();
      if (fileExtension === '.csv') {
        reader.readAsText(selectedFile);
      } else {
        reader.readAsArrayBuffer(selectedFile);
      }
    });
  };

  const handleFileSelect = async (selectedFile) => {
    if (!selectedFile) {
      return;
    }

    const validExtensions = ['.xlsx', '.xls', '.csv'];
    const fileExtension = selectedFile.name
      .substring(selectedFile.name.lastIndexOf('.'))
      .toLowerCase();

    if (!validExtensions.includes(fileExtension)) {
      setValidationError('Please upload a valid Excel file (.xlsx, .xls, or .csv)');
      setFile(null);
      return;
    }

    setValidationError(null);
    try {
      await validateFileFormat(selectedFile);
      setFile(selectedFile);
      setValidationError(null);
    } catch (error) {
      setValidationError(error);
      setFile(null);
    }
  };

  const handleFileInputChange = (e) => {
    const selectedFile = e.target.files[0];
    handleFileSelect(selectedFile);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    handleFileSelect(droppedFile);
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleUpload = async () => {
    if (!file) {
      setValidationError('Please select a file to upload');
      return;
    }
    if (onUpload) {
      await onUpload(file);
    }
  };

  const handleClose = () => {
    setFile(null);
    setIsDragging(false);
    setValidationError(null);
    onClose();
  };

  const handleDownloadTemplate = (e) => {
    e.stopPropagation();
    const poNumber = selectedPO?.po || 'PO_NUMBER';
    const headers = getRingsDetailTemplateHeaders();
    const csvContent = headers.join(',') + '\n';
    const sampleRow = [
      poNumber,
      'SITE_ID',
      'Project Name',
      'BP',
      'City',
      'Region',
      'Ring Name',
      '0',
      '0',
      '0',
    ].join(',');
    const fullContent = csvContent + sampleRow + '\n';
    const blob = new Blob([fullContent], {
      type: 'text/csv;charset=utf-8;',
    });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'PO_Rings_Detail_Template.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className={styles['modal-overlay']} onClick={handleClose}>
      <div className={styles['modal-content']} onClick={(e) => e.stopPropagation()}>
        <div className={styles['modal-header']}>
          <h2 className={styles['modal-title']}>Upload rings detail</h2>
          <button className={styles['close-button']} onClick={handleClose}>
            <CloseIcon />
          </button>
        </div>

        {selectedPO && (
          <div
            style={{
              padding: '8px 0',
              fontSize: '14px',
              color: '#666',
              marginBottom: '8px',
            }}
          >
            Selected PO: <strong>{selectedPO.po}</strong>
          </div>
        )}

        <div className={styles['modal-body']}>
          <div
            className={`${styles['upload-area']} ${isDragging ? styles['upload-area-dragging'] : ''} ${file ? styles['upload-area-has-file'] : ''}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={handleClick}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={handleFileInputChange}
              style={{ display: 'none' }}
            />
            <div className={styles['upload-icon-container']}>
              <AddIcon className={styles['upload-icon']} />
            </div>
            <p className={styles['upload-text']}>Click or drag to this area to upload</p>
            <p className={styles['download-text']}>
              All rows must have PO Number equal to <strong>{selectedPO?.po ?? '—'}</strong>.
              Download format from{' '}
              <span className={styles['download-link']} onClick={handleDownloadTemplate}>
                here
              </span>
            </p>
          </div>
          {validationError && (
            <div className={styles['error-message']}>
              <p>{validationError}</p>
            </div>
          )}
          {file && (
            <div className={styles['file-display']}>
              <span className={styles['file-name']}>{file.name}</span>
              <button
                className={styles['remove-file-btn']}
                onClick={(e) => {
                  e.stopPropagation();
                  setFile(null);
                  setValidationError(null);
                }}
              >
                <CloseIcon />
              </button>
            </div>
          )}
        </div>

        <div className={styles['modal-footer']}>
          <button
            className={styles['upload-btn']}
            onClick={handleUpload}
            disabled={isUploading || !file}
          >
            {isUploading ? 'Uploading...' : 'Upload'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UploadRingsDetailModal;
